import $ from 'jquery'
import _ from 'lodash'
import constants from 'scoreboard/components/constants'
import Backbone from 'backbone'
import layoutManager from 'scoreboard/components/layout-manager'
import mobile from 'scoreboard/components/mobile'
import conductor from 'scoreboard/components/conductor'

/**
 * The Briefings Layout Manager handles briefings specific transitions. This manager
 * switches the app between the various briefing modes (constants.js). This manager
 * can also be used by other components/views in detecting what briefing state the app
 * might be in.
 **/
class BriefingsLayoutManager {

	setup() {
		this.body = $('body')
		this.navDisplayPane = $('.nav-display-pane')
		this.navPane = $('.nav-pane')
		this.headPane = $('.head-pane')
		this.projectOrganizationMenu = $('.project-organization-menu')
		this.menuWrapper = $('.menu-wrapper')
		layoutManager.on('resize', () => this.resize())
	}

	/**
	 * Toggles the 'Start/Stop' state of the briefing page.
	 * @param {Boolean} start True if the briefing is to enter the start mode, else false.
	 */
	toggleStart(start) {
		return new Promise(resolve => {
			// Theres a special case we have to handle here:
			// If we're entering the standard layout after having been in the
			// LAYOUT_BRIEFING_PRESENTATION which was invoked while in the mobile orientation,
			// since the nav/head/orgPaneToggler weren't collapsed like normal, we
			// have to reset their positioning and THEN transition them out to visibility.
			if (this.needsMobileMenuAdjustment && !start && !mobile.isMobileOrientation()) {
				layoutManager.toggleTransitions(false)
				this.projectOrganizationMenu.css({
					'left': 0,
					'width': this.headPane.width()
				})
				this.projectOrganizationMenu.css('margin-inline-start', -this.projectOrganizationMenu.width())
				this.headPane.css('margin-inline-start', -this.headPane.width())
				this.menuWrapper.css('inset-inline-start', 0)
				this.navPane.css('inset-inline-start', -this.navPane.width())
				this.body.addClass('is-head-pane-hidden')
				layoutManager.toggleTransitions(true)
				this.needsMobileMenuAdjustment = false
			}

			this.body.addClass('is-toggling-briefings-mode')
			if (start) {
				this.body.addClass(constants.BRIEFING_MODE_PRESENTATION)
				layoutManager.toggleNavCollapsed(start).then(() => {
					this.toggleHeadHidden(start).then(() => {
						this.needsMobileMenuAdjustment = mobile.isMobileOrientation()
						this.body.removeClass('is-toggling-briefings-mode')
						resolve()
						Backbone.trigger('reset:display')
					})
				})
			} else {
				this.toggleHeadHidden(start).then(() => {
					layoutManager.toggleNavCollapsed(start).then(() => {
						this.body
							.removeClass(constants.BRIEFING_MODE_PRESENTATION)
							.removeClass(constants.BRIEFING_MODE_IS_PAUSED)
							.removeClass('is-toggling-briefings-mode')
						if (conductor.section() !== 'briefings') {
							this.toggleBriefingLayout(false)
						}
						resolve()
						Backbone.trigger('reset:display')
					})
				})
			}
		})
	}

	/**
	 * Toggles the paused mode of the briefing page.
	 * @param {Boolean} pause True if the briefing is to enter the paused mode, else false.
	 */
	togglePaused(pause) {
		return new Promise(resolve => {
			this.body.addClass('is-toggling-briefings-mode')
			if (pause === this.isPaused()) {
				resolve()
				this.body.removeClass('is-toggling-briefings-mode')
				return
			}

			// If we're pausing the briefing, immediately append the constants.BRIEFING_MODE_IS_PAUSED
			// class so that the top bar is in place for the entire animation and then
			// Hide the head/nav panes.
			if (pause) {
				this.body.addClass(constants.BRIEFING_MODE_IS_PAUSED)
				this.toggleHeadHidden(false).then(() => {
					layoutManager.toggleNavCollapsed(false).then(() => {
						this.body.removeClass('is-toggling-briefings-mode')
						resolve()
						Backbone.trigger('reset:display')
					})
				})
			// If we're un-pausing the briefing, show the head/nav panes and
			// then remove the constants.BRIEFING_MODE_IS_PAUSED class
			} else {
				layoutManager.toggleNavCollapsed(true).then(() => {
					this.toggleHeadHidden(true).then(() => {
						this.body
							.removeClass(constants.BRIEFING_MODE_IS_PAUSED)
							.removeClass('is-toggling-briefings-mode')
						resolve()
						Backbone.trigger('reset:display')
					})
				})
			}
		})
	}

	/**
	 * Toggles whether the .head-pane is hidden off to the left. It's different
	 * from the .head-pane being collapse because here, it's not collapsed but instead totally
	 * out of range of visibility. Used while entering/exiting BRIEFING_MODE_PRESENTATION
	 * @param {Boolean} hide True if the .head-pane should be hidden, else false.
	 */
	toggleHeadHidden(hide) {
		return new Promise((resolve) => {

			// Under these circumstances, we shouldn't hide the .head-pane
			// - It's already hidden
			// - We're in the mobile orientation (because the head pane is
			//   already setup for the mobile menu)
			let isHidden = this.body.hasClass('is-head-pane-hidden')
			if (hide == isHidden || mobile.isMobileOrientation()) {
				resolve()
				return
			}

			this.body.toggleClass('is-head-pane-hidden', hide)

			// If app transitions are enabled, wait till the .head-pane finishes
			// it's transition to resolve the promise. Else resolve it at the next tick.
			if (layoutManager.transitionsEnabled()) {
				this.headPane.one('transitionend', resolve)
			} else {
				process.nextTick(resolve)
			}

			if (hide) {
				this.projectOrganizationMenu.css('margin-inline-start', -this.projectOrganizationMenu.width())
				this.headPane.css('margin-inline-start', -this.headPane.width())
				this.navDisplayPane.css('margin-inline-start', 0)
				this.headPane.addClass('is-head-hidden')
			} else {
				this.projectOrganizationMenu.css('margin-inline-start', 0)
				this.headPane.css('margin-inline-start', 0)
				this.navDisplayPane.css('margin-inline-start', this.headPane.width())
				this.headPane.removeClass('is-head-hidden')
			}
		})
	}

	/**
	 * Toggles the .layout-briefing class. This class is
	 * responsible for the styles which show the slide controls and push the
	 * content down to appear as a briefing should.
	 * @param {Boolean} isBriefingLayout True if the we want the briefing look, else false.
	 */
	toggleBriefingLayout(isBriefingLayout) {
		this.body.toggleClass('layout-briefing', isBriefingLayout)
	}

	/**
	 * See whether we're in the briefings paused mode.
	 * @return {Boolean} True if we're in the briefings paused mode, else false.
	 */
	isPaused() {
		return this._getBriefingMode().indexOf(constants.BRIEFING_MODE_IS_PAUSED) >= 0
	}

	/**
	 * See whether we're in the briefings presentation mode.
	 * @return {Boolean} True if we're in the briefings presentation mode, else false.
	 */
	isBriefingPresentationMode() {
		return this.body.hasClass(constants.BRIEFING_MODE_PRESENTATION)
	}

	/**
	 * Discern the current briefing mode..
	 * @return {String} Class representing the currently active briefing mode.
	 */
	_getBriefingMode() {
		return _.intersection([
			constants.BRIEFING_MODE_IS_PAUSED,
			constants.BRIEFING_MODE_IS_EDITING_SLIDE,
			constants.BRIEFING_MODE_IS_EDITING_BRIEFING,
			constants.BRIEFING_MODE_PRESENTATION
		], this.body[0].classList)
	}

	/**
	 * The resize method here is invoked by way of the resize method in the layoutManager.
	 * No DOM manipulation is done for briefings in the layoutManager, but rather here.
	 */
	resize() {
		// If we're:
		// - In the briefings presentation mode
		// - In the mobile orientation
		// - And the mobile menu is not open
		// be sure to keep the mobile menu out of the way.
		if (this.isBriefingPresentationMode()
			&& mobile.isMobileOrientation()
			&& !mobile.isMobileMenuVisible()) {
			layoutManager.toggleTransitions(false)
			this.navPane.css('inset-inline-start', -constants.MOBILE_MENU_WIDTH)
			this.projectOrganizationMenu.css('margin-inline-start', -this.projectOrganizationMenu.width())
			layoutManager.toggleTransitions(true)
		}

		// If we're:
		// - Not in the briefings presentation mode
		// - Not in the briefings paused mode
		// - Not in the mobile orientation
		// set the head/nav panes collapsed state. This makes it so that the the
		// panes are correctly reset after having been in the mobile orientation,
		// triggering the menus.
		if (!this.isBriefingPresentationMode()
			&& !this.isPaused()
			&& !mobile.isMobileOrientation()) {
			layoutManager.toggleTransitions(false)
			layoutManager.toggleNavCollapsed(layoutManager.isNavCollapsed())
			this.toggleHeadHidden(false)
			layoutManager.toggleTransitions(true)
		}

		// If we're:
		// - In the briefings presentation mode
		// - In the mobile orientation
		// make sure the nav pane sheds it's is-collapsed class from when it was
		// collapsed while getting into presentation mode. Without this, the navPane
		// will keep it's left margin which pulls it out of view in the mobile menu.
		if (this.isBriefingPresentationMode()
			&& mobile.isMobileOrientation()) {
			this.navPane.removeClass('is-collapsed')
		}

		// If we're:
		// - Not in the briefings presentation mode
		// - In the mobile orientation
		// make sure we nix the left margin from when the head pane was hidden while
		// possibly transitioning into presentation mode. This prevents distortions
		// from happening when going from mobile to the desktop orientation.
		if (!this.isBriefingPresentationMode()
			&& mobile.isMobileOrientation()) {
			this.headPane.css('margin-inline-start', '')
		}
	}
}

_.extend(BriefingsLayoutManager.prototype, Backbone.Events)

var briefingsLayoutManager = new BriefingsLayoutManager()
export default briefingsLayoutManager
export { BriefingsLayoutManager }

briefingsLayoutManager.__filename__ = 'briefings/briefings-layout-manager.js'
